/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  memo,
} from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Text,
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  useToast,
  FormErrorMessage,
  Box,
  Icon,
  Spinner,
  StackDivider,
} from "@chakra-ui/react";
import { FiSearch, FiChevronRight, FiChevronLeft } from "react-icons/fi";
import api from "utils/api";

const VehicleRow = memo(function VehicleRow({ arac, checked, onToggle }) {
  const pid = arac.id;
  const plaka = (arac.plaka || "").trim() || "—";
  const imei = (arac.imei || "").trim() || String(pid);
  const line = `${plaka} (${imei})`;
  return (
    <HStack
      role="button"
      tabIndex={0}
      cursor="pointer"
      px={2}
      py={2}
      spacing={3}
      borderRadius="sm"
      bg={checked ? "blue.50" : "transparent"}
      _hover={{ bg: checked ? "blue.50" : "gray.50" }}
      onClick={() => onToggle(pid)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle(pid);
        }
      }}
    >
      <Checkbox
        isChecked={checked}
        isReadOnly
        pointerEvents="none"
        colorScheme="blue"
        size="sm"
      />
      <Text fontSize="sm" color="gray.800" noOfLines={2} flex={1} minW={0}>
        {line}
      </Text>
    </HStack>
  );
});

const SearchResultRow = memo(function SearchResultRow({ arac, onToggle }) {
  const plaka = (arac.plaka || "").trim() || "—";
  const imei = (arac.imei || "").trim() || String(arac.id);
  return (
    <HStack
      role="button"
      tabIndex={0}
      px={3}
      py={2}
      cursor="pointer"
      justify="space-between"
      _hover={{ bg: "gray.50" }}
      spacing={3}
      onClick={() => onToggle(arac.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle(arac.id);
        }
      }}
    >
      <Text fontSize="sm" color="gray.800" noOfLines={1}>
        {plaka} ({imei})
      </Text>
    </HStack>
  );
});

function EkHesapEkleModal({ isOpen, onClose, firmaId, firmaAdi, refreshList }) {
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [araclarLoading, setAraclarLoading] = useState(false);
  const [araclar, setAraclar] = useState([]);
  const [aracFilter, setAracFilter] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [formData, setFormData] = useState({
    ek_hesap_adi: "",
    email: "",
    telefon_no: "",
    secilen_arac_ids: [],
    can_create_sub_users: false,
  });
  const [errors, setErrors] = useState({});

  const selectedSet = useMemo(
    () => new Set(formData.secilen_arac_ids),
    [formData.secilen_arac_ids]
  );

  const normalizedFilter = aracFilter.trim().toLowerCase();

  const filteredAraclar = useMemo(() => {
    if (!normalizedFilter) return araclar;
    return araclar.filter((a) => {
      const plaka = (a.plaka || "").toLowerCase();
      const imei = (a.imei || "").toLowerCase();
      return plaka.includes(normalizedFilter) || imei.includes(normalizedFilter);
    });
  }, [araclar, normalizedFilter]);

  const selectedAraclar = useMemo(() => {
    if (!araclar.length || !formData.secilen_arac_ids.length) return [];
    return araclar.filter((a) => selectedSet.has(a.id));
  }, [araclar, formData.secilen_arac_ids.length, selectedSet]);

  const searchResults = useMemo(() => {
    if (!isSearchFocused) return [];
    const source = normalizedFilter ? filteredAraclar : araclar;
    return source.filter((a) => !selectedSet.has(a.id)).slice(0, 100);
  }, [araclar, filteredAraclar, isSearchFocused, normalizedFilter, selectedSet]);

  const loadFirmaAraclari = useCallback(async () => {
    if (!firmaId) return;
    setAraclarLoading(true);
    try {
      const response = await api.get(`/api/devices?firma_id=${firmaId}`);
      const result = await response.json();
      setAraclar(response.ok && Array.isArray(result) ? result : []);
    } catch {
      setAraclar([]);
    } finally {
      setAraclarLoading(false);
    }
  }, [firmaId]);

  useEffect(() => {
    if (!isOpen || !firmaId) return;
    setStep(1);
    setAracFilter("");
    setIsSearchFocused(false);
    setFormData({
      ek_hesap_adi: "",
      email: "",
      telefon_no: "",
      secilen_arac_ids: [],
      can_create_sub_users: false,
    });
    setErrors({});
    loadFirmaAraclari();
  }, [isOpen, firmaId, loadFirmaAraclari]);

  const handleInputChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev)   => (prev[field] ? { ...prev, [field]: undefined } : prev));
  }, []);

  const toggleArac = useCallback((aracId) => {
    setFormData((prev) => {
      const next = new Set(prev.secilen_arac_ids);
      if (next.has(aracId)) next.delete(aracId);
      else next.add(aracId);
      return { ...prev, secilen_arac_ids: [...next] };
    });
    setErrors((prev) =>
      "secilen_arac_ids" in prev
        ? { ...prev, secilen_arac_ids: undefined }
        : prev
    );
  }, []);

  const validateStep1 = useCallback(() => {
    const e = {};
    if (!formData.ek_hesap_adi.trim()) e.ek_hesap_adi = "Hesap adı zorunludur";
    if (!formData.email.trim()) e.email = "E-posta zorunludur";
    else if (!/^\S+@\S+\.\S+$/.test(formData.email.trim()))
      e.email = "Geçerli bir e-posta girin";
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [formData]);

  const validateStep2 = useCallback(() => {
    setErrors({});
    return true;
  }, []);

  const goNext = () => {
    if (validateStep1()) setStep(2);
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    setLoading(true);
    try {
      const response = await api.post(`/api/firmalar/ek-hesap-olustur`, {
        ana_firma_id: firmaId,
        ek_hesap_adi: formData.ek_hesap_adi,
        email: formData.email.trim(),
        telefon_no: formData.telefon_no.trim(),
        secilen_arac_ids: formData.secilen_arac_ids,
        userLimit: formData.can_create_sub_users ? 50 : -1,
      });

      if (!response.ok) {
        const errorData = await response.json();
        const detail = errorData?.detail;
        const low = String(detail || "").toLowerCase();
        if (low.includes("e-posta") && (low.includes("kullan") || low.includes("kayıt"))) {
          throw new Error("Bu e-posta zaten kullanımda. Lütfen farklı bir e-posta girin.");
        }
        throw new Error(detail || "Hesap oluşturulamadı");
      }

      const result = await response.json();
      toast({
        title: "Tamam",
        description: result.message || "Alt hesap oluşturuldu.",
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
      refreshList?.();
      onClose();
    } catch (error) {
      toast({
        title: "Hata",
        description: error.message || "İşlem başarısız.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const step1Valid =
    formData.ek_hesap_adi.trim() &&
    formData.email.trim();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      size="md"
      isCentered
      motionPreset="none"
      scrollBehavior="inside"
      closeOnOverlayClick={!loading}
    >
      <ModalOverlay bg="blackAlpha.500" />
      <ModalContent
        borderRadius="xl"
        boxShadow="lg"
        maxH="85vh"
        display="flex"
        flexDirection="column"
        mx={3}
      >
        <ModalCloseButton size="sm" isDisabled={loading} />

        <Box px={5} pt={5} pb={3} borderBottomWidth="1px" borderColor="gray.100">
          <Text fontSize="md" fontWeight="semibold" color="gray.800">
            Alt hesap oluştur
          </Text>
          <HStack fontSize="sm" color="gray.600" mt={1} spacing={1}>
            <Text as="span" color="gray.500">Firma:</Text>
            <Text as="span" fontWeight="medium" color="gray.800">{firmaAdi || "—"}</Text>
          </HStack>
          <HStack mt={3} spacing={2}>
            <Box
              h={1}
              flex={1}
              borderRadius="full"
              bg={step >= 1 ? "blue.500" : "gray.200"}
            />
            <Box
              h={1}
              flex={1}
              borderRadius="full"
              bg={step >= 2 ? "blue.500" : "gray.200"}
            />
          </HStack>
          <Text fontSize="xs" color="gray.500" mt={1}>
            {step === 1 ? "1 — İletişim bilgileri" : "2 — Araç erişimi"}
          </Text>
        </Box>

        <ModalBody flex="1" overflowY="auto" px={5} py={4}>
          {step === 1 && (
            <VStack align="stretch" spacing={4}>
              <FormControl isInvalid={!!errors.ek_hesap_adi}>
                <FormLabel fontSize="sm" mb={1}>
                  İsim
                </FormLabel>
                <Input
                  size="sm"
                  placeholder="Örn: Operasyon, Ankara Şube"
                  value={formData.ek_hesap_adi}
                  onChange={(e) => handleInputChange("ek_hesap_adi", e.target.value)}
                  autoComplete="off"
                />
                <FormErrorMessage fontSize="xs">{errors.ek_hesap_adi}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.email}>
                <FormLabel fontSize="sm" mb={1}>
                  E-posta
                </FormLabel>
                <Input
                  size="sm"
                  type="email"
                  inputMode="email"
                  placeholder="ornek@sirket.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  autoComplete="off"
                />
                <FormErrorMessage fontSize="xs">{errors.email}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.telefon_no}>
                <FormLabel fontSize="sm" mb={1}>
                  Telefon
                </FormLabel>
                <Input
                  size="sm"
                  type="tel"
                  inputMode="tel"
                  placeholder="05xxxxxxxxx"
                  value={formData.telefon_no}
                  onChange={(e) => handleInputChange("telefon_no", e.target.value.replace(/\s+/g, ""))}
                  autoComplete="off"
                />
                <FormErrorMessage fontSize="xs">{errors.telefon_no}</FormErrorMessage>
              </FormControl>
            </VStack>
          )}

          {step === 2 && (
            <VStack align="stretch" spacing={3}>
              <Box
                px={3}
                py={2.5}
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                bg="gray.50"
              >
                <Checkbox
                  isChecked={formData.can_create_sub_users}
                  onChange={(e) => handleInputChange("can_create_sub_users", e.target.checked)}
                  colorScheme="blue"
                  size="sm"
                >
                  <Text as="span" fontSize="sm" fontWeight="600" color="gray.800">
                    Kullanıcı oluşturabilir
                  </Text>
                </Checkbox>
              </Box>

              <Box
                position="relative"
                borderWidth="1px"
                borderColor="blue.400"
                borderRadius="md"
                bg="white"
                px={3}
                pt={4}
                pb={2}
              >
                <Text
                  position="absolute"
                  top="-10px"
                  left={3}
                  bg="white"
                  px={1.5}
                  fontSize="xs"
                  fontWeight="600"
                  color="blue.600"
                  letterSpacing="0.02em"
                >
                  Araçlar
                </Text>
                <Box position="relative" mb={2}>
                  <HStack spacing={2}>
                    <Input
                      size="sm"
                      placeholder="Plaka veya IMEI ile ara..."
                      value={aracFilter}
                      onChange={(e) => setAracFilter(e.target.value)}
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={() => {
                        setTimeout(() => setIsSearchFocused(false), 120);
                      }}
                      autoComplete="off"
                      bg="white"
                    />
                    <Icon as={FiSearch} color="gray.400" boxSize={5} flexShrink={0} />
                  </HStack>
                  {isSearchFocused ? (
                    <Box
                      position="absolute"
                      left={0}
                      right={10}
                      top="calc(100% + 6px)"
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      bg="white"
                      zIndex={20}
                      boxShadow="sm"
                      maxH="200px"
                      overflowY="auto"
                    >
                      <VStack
                        align="stretch"
                        spacing={0}
                        divider={<StackDivider borderColor="gray.100" />}
                      >
                        {searchResults.length === 0 ? (
                          <Text fontSize="sm" color="gray.500" textAlign="center" py={3}>
                            {selectedSet.size > 0
                              ? "Seçili olmayan araç bulunamadı."
                              : "Aramaya uygun araç yok."}
                          </Text>
                        ) : (
                          searchResults.slice(0, 5).map((arac) => (
                            <SearchResultRow
                              key={`search-${arac.id}`}
                              arac={arac}
                              onToggle={toggleArac}
                            />
                          ))
                        )}
                      </VStack>
                    </Box>
                  ) : null}
                </Box>
                <Text fontSize="xs" color="gray.500" mb={1}>
                  {normalizedFilter
                    ? `${filteredAraclar.length} / ${araclar.length} araç · ${selectedSet.size} seçili`
                    : `${selectedSet.size} seçili`}
                </Text>
                <Text fontSize="xs" color="gray.600" mb={1} fontWeight="600">
                  Görüntülenen araçlar
                </Text>
                {araclarLoading ? (
                  <HStack py={8} justify="center">
                    <Spinner size="md" color="blue.500" />
                  </HStack>
                ) : araclar.length === 0 ? (
                  <Text fontSize="sm" color="gray.500" py={6} textAlign="center">
                    Bu firmaya bağlı araç yok.
                  </Text>
                ) : (
                  <VStack
                    align="stretch"
                    spacing={0}
                    maxH="240px"
                    overflowY="auto"
                    pr={1}
                    divider={<StackDivider borderColor="gray.100" />}
                  >
                    {selectedAraclar.length === 0 ? (
                      <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                        Henüz görüntülenen araç seçilmedi.
                      </Text>
                    ) : (
                      selectedAraclar.map((arac) => (
                        <VehicleRow
                          key={arac.id}
                          arac={arac}
                          checked={selectedSet.has(arac.id)}
                          onToggle={toggleArac}
                        />
                      ))
                    )}
                  </VStack>
                )}
              </Box>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter
          borderTopWidth="1px"
          borderColor="gray.100"
          py={3}
          px={5}
          gap={2}
          flexWrap="wrap"
        >
          {step === 1 ? (
            <>
              <Button variant="ghost" size="sm" onClick={handleCancel} isDisabled={loading}>
                İptal
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                rightIcon={<FiChevronRight />}
                onClick={goNext}
                isDisabled={!step1Valid}
              >
                Devam
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<FiChevronLeft />}
                onClick={() => setStep(1)}
                isDisabled={loading}
              >
                Geri
              </Button>
              <Button
                size="sm"
                colorScheme="green"
                onClick={handleSubmit}
                isLoading={loading}
                loadingText="Oluşturuluyor"
              >
                Oluştur
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default EkHesapEkleModal;
